import { useEffect, useState, useRef } from 'react';
import { useTour } from '@/contexts/TourContext';
import { Button } from '@/components/ui/button';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

export function TourOverlay() {
  const { isActive, currentStep, currentStepIndex, totalSteps, nextStep, prevStep, endTour } = useTour();
  const [targetRect, setTargetRect] = useState<Rect | null>(null);
  const [tooltipStyle, setTooltipStyle] = useState<React.CSSProperties>({});
  const tooltipRef = useRef<HTMLDivElement>(null);
  const rafRef = useRef<number>();

  useEffect(() => {
    if (!isActive || !currentStep) {
      setTargetRect(null);
      return;
    }

    const update = () => {
      const el = document.querySelector(currentStep.target);
      if (el) {
        const rect = el.getBoundingClientRect();
        setTargetRect({
          top: rect.top,
          left: rect.left,
          width: rect.width,
          height: rect.height,
        });
        // Scroll element into view if needed
        el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      } else {
        setTargetRect(null);
      }
    };

    // Initial + poll for dynamic elements
    update();
    const interval = setInterval(update, 500);
    return () => clearInterval(interval);
  }, [isActive, currentStep]);

  // Position tooltip relative to target
  useEffect(() => {
    if (!targetRect || !tooltipRef.current) return;

    const tooltip = tooltipRef.current;
    const tw = tooltip.offsetWidth;
    const th = tooltip.offsetHeight;
    const pad = 16;
    const placement = currentStep?.placement ?? 'bottom';

    let top = 0;
    let left = 0;

    switch (placement) {
      case 'bottom':
        top = targetRect.top + targetRect.height + pad;
        left = targetRect.left + targetRect.width / 2 - tw / 2;
        break;
      case 'top':
        top = targetRect.top - th - pad;
        left = targetRect.left + targetRect.width / 2 - tw / 2;
        break;
      case 'left':
        top = targetRect.top + targetRect.height / 2 - th / 2;
        left = targetRect.left - tw - pad;
        break;
      case 'right':
        top = targetRect.top + targetRect.height / 2 - th / 2;
        left = targetRect.left + targetRect.width + pad;
        break;
    }

    // Keep within viewport
    left = Math.max(12, Math.min(left, window.innerWidth - tw - 12));
    top = Math.max(12, Math.min(top, window.innerHeight - th - 12));

    setTooltipStyle({ top, left });
  }, [targetRect, currentStep?.placement]);

  if (!isActive || !currentStep) return null;

  const spotlightPad = 8;

  return (
    <div className="fixed inset-0 z-[9999] pointer-events-auto">
      {/* Dark overlay with cutout */}
      <svg className="absolute inset-0 w-full h-full" style={{ pointerEvents: 'none' }}>
        <defs>
          <mask id="tour-mask">
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            {targetRect && (
              <rect
                x={targetRect.left - spotlightPad}
                y={targetRect.top - spotlightPad}
                width={targetRect.width + spotlightPad * 2}
                height={targetRect.height + spotlightPad * 2}
                rx="12"
                fill="black"
              />
            )}
          </mask>
        </defs>
        <rect
          x="0" y="0" width="100%" height="100%"
          fill="hsl(var(--foreground) / 0.55)"
          mask="url(#tour-mask)"
        />
      </svg>

      {/* Pulsing ring around target */}
      {targetRect && (
        <div
          className="absolute rounded-xl border-2 border-primary animate-pulse pointer-events-none"
          style={{
            top: targetRect.top - spotlightPad,
            left: targetRect.left - spotlightPad,
            width: targetRect.width + spotlightPad * 2,
            height: targetRect.height + spotlightPad * 2,
            boxShadow: '0 0 0 4px hsl(var(--primary) / 0.25)',
          }}
        />
      )}

      {/* Click-blocker outside spotlight */}
      <div className="absolute inset-0" onClick={endTour} />

      {/* Tooltip */}
      <div
        ref={tooltipRef}
        className="absolute z-10 w-80 bg-card border border-border rounded-xl shadow-xl p-5 pointer-events-auto"
        style={tooltipStyle}
      >
        {/* Close */}
        <button
          onClick={endTour}
          className="absolute top-3 right-3 text-muted-foreground hover:text-foreground transition-colors"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Progress dots */}
        <div className="flex items-center gap-1.5 mb-3">
          {Array.from({ length: totalSteps }).map((_, i) => (
            <div
              key={i}
              className={cn(
                'h-1.5 rounded-full transition-all duration-300',
                i === currentStepIndex
                  ? 'w-6 bg-primary'
                  : i < currentStepIndex
                    ? 'w-1.5 bg-primary/50'
                    : 'w-1.5 bg-muted-foreground/30',
              )}
            />
          ))}
          <span className="ml-auto text-xs text-muted-foreground">
            {currentStepIndex + 1}/{totalSteps}
          </span>
        </div>

        <h3 className="text-sm font-semibold text-foreground mb-1">{currentStep.title}</h3>
        <p className="text-sm text-muted-foreground leading-relaxed mb-4">{currentStep.description}</p>

        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            size="sm"
            onClick={prevStep}
            disabled={currentStepIndex === 0}
            className="gap-1"
          >
            <ChevronLeft className="w-3.5 h-3.5" />
            Tilbage
          </Button>
          <Button
            size="sm"
            onClick={nextStep}
            className="gap-1"
          >
            {currentStepIndex === totalSteps - 1 ? 'Afslut' : 'Næste'}
            {currentStepIndex < totalSteps - 1 && <ChevronRight className="w-3.5 h-3.5" />}
          </Button>
        </div>
      </div>
    </div>
  );
}
